import { useState } from 'react';
import { Link } from 'react-router';
import { useDeadLetters, useReplayDeadLetter } from '../api/queries.js';
import { Badge, Button, Card, Loading, Mono, Page, Problem, Table } from '../components/ui.jsx';
import { formatDateTime, formatNumber } from '../utils/format.js';

/**
 * Where a message ends up when a consumer could not handle it, after every retry the channel
 * allowed. Replaying publishes a *new* message with the same payload and the original as its
 * cause: the failure stays in history, and the fix is visible next to what it fixed.
 */
export function DeadLetters() {
  const [openOnly, setOpenOnly] = useState(true);
  const deadLetters = useDeadLetters(openOnly);
  const replay = useReplayDeadLetter();

  if (deadLetters.isLoading) return <Loading what="le dead letter" />;
  if (deadLetters.error) return <Problem error={deadLetters.error} />;

  return (
    <Page
      eyebrow="OSSERVA"
      title="Dead Letters"
      description="I messaggi che nessun consumatore è riuscito a elaborare."
      actions={
        <label className="checkbox">
          <input type="checkbox" checked={openOnly} onChange={(event) => setOpenOnly(event.target.checked)} />
          Solo non ancora riprovate
        </label>
      }
    >
      <Card title={`${formatNumber(deadLetters.data!.total)} dead letter`}>
        {replay.error ? <Problem error={replay.error} /> : null}
        <Table
          head={
            <tr>
              <th>Quando</th>
              <th>Canale</th>
              <th>Consumatore</th>
              <th>Produttore</th>
              <th className="right">Tentativi</th>
              <th>Motivo</th>
              <th />
            </tr>
          }
          empty={openOnly ? 'Nessuna dead letter aperta. Va tutto bene.' : 'Nessuna dead letter registrata.'}
        >
          {deadLetters.data!.deadLetters.map((deadLetter) => (
            <tr key={deadLetter.id}>
              <td className="muted small">{formatDateTime(deadLetter.deadLetteredAt)}</td>
              <td>{deadLetter.channel}</td>
              <td>{deadLetter.consumer}</td>
              <td>{deadLetter.producer}</td>
              <td className="right">{formatNumber(deadLetter.attempts)}</td>
              <td className="muted small reason">{deadLetter.reason}</td>
              <td className="row-actions">
                {deadLetter.replayedAt ? (
                  <span className="muted small">
                    riprovata{' '}
                    {deadLetter.replayMessageId ? (
                      <Link to={`/messages/${deadLetter.replayMessageId}`}>
                        <Mono value={deadLetter.replayMessageId.slice(0, 8)} />
                      </Link>
                    ) : null}
                  </span>
                ) : (
                  <Button
                    variant="primary"
                    disabled={replay.isPending}
                    onClick={() => {
                      void replay.mutateAsync(deadLetter.id);
                    }}
                  >
                    Riprova
                  </Button>
                )}
                <Link to={`/messages/${deadLetter.messageId}`}>
                  <Badge tone="info">messaggio</Badge>
                </Link>
              </td>
            </tr>
          ))}
        </Table>
      </Card>
    </Page>
  );
}
